import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Pencil, X } from "lucide-react";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import useAuth from "@/hooks/useAuth";
import {
  createCustomer,
  updateCustomer,
} from "@/redux/slice/customer-slice/customer-slice";
import FormInputField from "../lib/from-input-field";

const customerSchema = z.object({
  full_name: z.string().min(1, "Full Name is required"),
  number: z.string().min(1, "Phone Number is required"),
  email: z.string().email("Valid Email is required"),
});

const addressSchema = z.object({
  id: z.number().optional(),
  client_uuid: z.string().optional(),
  postal_code: z.string().min(1, "Post Code is required"),
  door_no: z.string().min(1, "Door No is required"),
  address: z.string().min(1, "Address is required"),
  city: z.string().min(1, "City is required"),
  is_active: z.boolean(),
});

const payloadSchema = z.object({
  restaurant_id: z.number(),
  full_name: z.string().min(1),
  number: z.string().min(1),
  email: z.string().email(),
  address: z.array(addressSchema).min(1, "At least one address is required"),
});

const inputClassName = "bg-cblack3";
const addressInputClassName = "bg-[#3A3B3D] border-white/20";

const generateAddressUuid = () =>
  globalThis.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

const createAddressDraft = (overrides = {}) => ({
  id: undefined,
  client_uuid: generateAddressUuid(),
  postal_code: "",
  door_no: "",
  address: "",
  city: "",
  note: "",
  is_active: true,
  ...overrides,
});

const ensureSingleActiveAddress = (list = []) => {
  if (!Array.isArray(list) || list.length === 0) return [];

  let activeFound = false;

  const normalized = list.map((address) => {
    const shouldBeActive = Boolean(address?.is_active) && !activeFound;

    if (shouldBeActive) {
      activeFound = true;
    }

    return {
      ...address,
      is_active: shouldBeActive,
    };
  });

  if (!activeFound) {
    normalized[0] = {
      ...normalized[0],
      is_active: true,
    };
  }

  return normalized;
};

const getInitialAddresses = (customerData) => {
  if (!customerData) return [];

  if (Array.isArray(customerData.addresses) && customerData.addresses.length > 0) {
    return ensureSingleActiveAddress(
      customerData.addresses.map((address) => ({
        id: address?.id,
        client_uuid: generateAddressUuid(),
        postal_code: address?.postal_code || "",
        door_no: address?.door_no || "",
        address: address?.address || address?.address_line_1 || "",
        city: address?.city || "",
        note: address?.note || "",
        is_active: Boolean(address?.is_active),
      })),
    );
  }

  if (customerData.active_address) {
    return ensureSingleActiveAddress([
      {
        id: customerData.active_address?.id,
        client_uuid: generateAddressUuid(),
        postal_code: customerData.active_address?.postal_code || "",
        door_no: customerData.active_address?.door_no || "",
        address: customerData.active_address?.address || customerData.active_address?.address_line_1 || "",
        city: customerData.active_address?.city || "",
        note: customerData.active_address?.note || "",
        is_active: true,
      },
    ]);
  }

  return [];
};

const hasAddressDraftValue = (draft) =>
  Boolean(
    draft?.postal_code?.trim() ||
      draft?.door_no?.trim() ||
      draft?.address?.trim() ||
      draft?.city?.trim() ||
      draft?.note?.trim(),
  );

function AddNewCustomerDialog({
  open,
  onOpenChange,
  customerData = null,
}) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { formLoading } = useSelector((state) => state.customer.customer);

  const [addressDraft, setAddressDraft] = useState(() => createAddressDraft());
  const [addresses, setAddresses] = useState([]);
  const [addressErrors, setAddressErrors] = useState({});
  const [editingAddressUuid, setEditingAddressUuid] = useState(null);

  const isEditMode = Boolean(customerData?.id);
  const restaurantId = Number(user?.restaurant_id || 1);

  const form = useForm({
    resolver: zodResolver(customerSchema),
    mode: "onChange",
    defaultValues: {
      full_name: "",
      number: "",
      email: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        full_name: customerData?.full_name || "",
        number: customerData?.number || "",
        email: customerData?.email || "",
      });

      const initialAddresses = getInitialAddresses(customerData);
      setAddresses(initialAddresses);
      setAddressDraft(
        createAddressDraft({
          is_active: initialAddresses.length === 0,
        }),
      );
      setEditingAddressUuid(null);
      setAddressErrors({});
    }
  }, [open, customerData, form]);

  const handleDraftChange = (key, value) => {
    setAddressDraft((prev) => ({ ...prev, [key]: value }));
    setAddressErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateAddressDraft = () => {
    const parsed = addressSchema.safeParse(addressDraft);
    if (parsed.success) {
      setAddressErrors({});
      return parsed.data;
    }

    const fieldErrors = {};
    parsed.error.issues.forEach((issue) => {
      const key = issue.path[0];
      if (typeof key === "string") {
        fieldErrors[key] = issue.message;
      }
    });
    setAddressErrors(fieldErrors);
    return null;
  };

  const upsertAddress = ({ baseAddresses, draftAddress, targetUuid }) => {
    const resolvedUuid = targetUuid || draftAddress.client_uuid || generateAddressUuid();
    const nextAddress = {
      ...draftAddress,
      client_uuid: resolvedUuid,
    };

    let updatedAddresses;

    if (targetUuid) {
      updatedAddresses = baseAddresses.map((item) =>
        item.client_uuid === targetUuid
          ? {
              ...item,
              ...nextAddress,
            }
          : item,
      );
    } else {
      updatedAddresses = [...baseAddresses, nextAddress];
    }

    if (nextAddress.is_active) {
      return updatedAddresses.map((item) => ({
        ...item,
        is_active: item.client_uuid === resolvedUuid,
      }));
    }

    return ensureSingleActiveAddress(updatedAddresses);
  };

  const handleAddAnotherAddress = () => {
    const validatedAddress = validateAddressDraft();
    if (!validatedAddress) return;

    setAddresses((prev) =>
      upsertAddress({
        baseAddresses: prev,
        draftAddress: validatedAddress,
        targetUuid: editingAddressUuid,
      }),
    );

    setAddressDraft(createAddressDraft({ is_active: false }));
    setEditingAddressUuid(null);
    setAddressErrors({});
  };

  const handleRemoveAddress = (clientUuid) => {
    setAddresses((prev) => {
      const filtered = prev.filter((item) => item.client_uuid !== clientUuid);
      return ensureSingleActiveAddress(filtered);
    });

    if (editingAddressUuid === clientUuid) {
      setEditingAddressUuid(null);
      setAddressDraft(createAddressDraft({ is_active: addresses.length <= 1 }));
      setAddressErrors({});
    }
  };

  const handleEditAddress = (clientUuid) => {
    const picked = addresses.find((item) => item.client_uuid === clientUuid);

    if (!picked) return;

    setAddressDraft({ ...picked });
    setEditingAddressUuid(clientUuid);
    setAddressErrors({});
  };

  const normalizeAddressForPayload = (address) => ({
    ...(isEditMode && address?.id ? { id: address.id } : {}),
    postal_code: address.postal_code.trim(),
    door_no: address.door_no.trim(),
    address: address.address.trim(),
    city: address.city.trim(),
    is_active: Boolean(address.is_active),
  });

  const onSubmit = async (values) => {
    let finalAddresses = [...addresses];

    if (hasAddressDraftValue(addressDraft)) {
      const validatedAddress = validateAddressDraft();
      if (!validatedAddress) return;

      finalAddresses = upsertAddress({
        baseAddresses: finalAddresses,
        draftAddress: validatedAddress,
        targetUuid: editingAddressUuid,
      });
    }

    if (finalAddresses.length === 0) {
      toast.error("At least one address is required");
      return;
    }

    finalAddresses = ensureSingleActiveAddress(finalAddresses);

    const payload = {
      restaurant_id: restaurantId,
      full_name: values.full_name.trim(),
      number: values.number.trim(),
      email: values.email.trim(),
      address: finalAddresses.map(normalizeAddressForPayload),
    };

    const validatedPayload = payloadSchema.safeParse(payload);

    if (!validatedPayload.success) {
      toast.error(validatedPayload.error.issues?.[0]?.message || "Validation failed");
      return;
    }

    try {
      const response = isEditMode
        ? await dispatch(
            updateCustomer({ customerId: customerData.id, payload: validatedPayload.data }),
          ).unwrap()
        : await dispatch(createCustomer(validatedPayload.data)).unwrap();

      toast.success(
        response?.message ||
          (isEditMode ? "Customer updated successfully" : "Customer added successfully"),
      );
      setEditingAddressUuid(null);
      onOpenChange(false);
    } catch (error) {
      toast.error(error || "Failed to save customer");
    }
  };



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[760px] bg-cblack4 p-0 max-h-[90vh] flex flex-col">
        <div className="flex-shrink-0 py-4 px-8 flex items-center justify-between">
          <DialogTitle className="text-lg font-medium text-cwhite">
            {isEditMode ? "Update Customer" : "Add New Customer"}
          </DialogTitle>
        </div>
        <Separator />

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto px-8 py-6 flex flex-col gap-6">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <FormInputField
                  name="full_name"
                  style={inputClassName}
                  placeholder="Enter Full Name"
                  form={form}
                  label="Full Name"
                  type="text"
                  required
                />

                <FormInputField
                  name="number"
                  style={inputClassName}
                  placeholder="Enter Phone Number"
                  form={form}
                  label="Phone Number"
                  type="number"
                  required
                />

                <FormInputField
                  name="email"
                  style={inputClassName}
                  placeholder="Enter Email Address"
                  form={form}
                  label="Email Address"
                  type="text"
                  required
                />

                {/* <div className="flex flex-col gap-2">
                  <Label className="text-cwhite">Restaurant ID</Label>
                  <Input
                    className={inputClassName}
                    value={restaurantId}
                    readOnly
                    disabled
                  />
                </div> */}
              </div>

              <h3 className="text-base font-medium leading-none text-cwhite">
                Address
              </h3>

              <div className="bg-[#2E2F31] px-4 py-5 md:px-6 space-y-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-[88px_1fr_auto] md:items-start md:gap-4">
                  <p className="text-base leading-none font-medium text-cwhite">
                    Postcode<span className="text-cred">*</span>
                  </p>

                  <Input
                    className={addressInputClassName}
                    placeholder="Post code"
                    value={addressDraft.postal_code}
                    onChange={(e) =>
                      handleDraftChange("postal_code", e.target.value)
                    }
                    type="text"
                  />

                  <Button
                    type="button"
                    className="h-10 bg-[#22C55E] px-4 text-white hover:bg-[#22C55E]/90"
                    onClick={handleAddAnotherAddress}
                  >
                    {editingAddressUuid ? "Update Address" : "Another"}
                  </Button>

                  {addressErrors.postal_code && (
                    <p className="text-xs text-cred md:col-start-2">
                      {addressErrors.postal_code}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-[88px_1fr] md:items-start md:gap-4">
                  <p className="text-base leading-none font-medium text-cwhite">
                    Door No
                  </p>

                  <Input
                    className={addressInputClassName}
                    placeholder="Door No"
                    value={addressDraft.door_no}
                    onChange={(e) => handleDraftChange("door_no", e.target.value)}
                    type="text"
                  />

                  {addressErrors.door_no && (
                    <p className="text-xs text-cred md:col-start-2">
                      {addressErrors.door_no}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-[88px_1fr] md:items-start md:gap-4">
                  <p className="text-base leading-none font-medium text-cwhite">
                    Address<span className="text-cred">*</span>
                  </p>

                  <div className="space-y-4">
                    <Input
                      className={addressInputClassName}
                      placeholder="Address"
                      value={addressDraft.address}
                      onChange={(e) =>
                        handleDraftChange("address", e.target.value)
                      }
                      type="text"
                    />

                    {addressErrors.address && (
                      <p className="text-xs text-cred">
                        {addressErrors.address}
                      </p>
                    )}

                    <Input
                      className={addressInputClassName}
                      placeholder="City"
                      value={addressDraft.city}
                      onChange={(e) => handleDraftChange("city", e.target.value)}
                      type="text"
                    />

                    {addressErrors.city && (
                      <p className="text-xs text-cred">{addressErrors.city}</p>
                    )}

                    <div className="flex items-center justify-between rounded-md border border-white/20 p-3">
                      <Label className="text-cwhite">Is Active</Label>
                      <Switch
                        checked={addressDraft.is_active}
                        onCheckedChange={(value) =>
                          handleDraftChange("is_active", Boolean(value))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {addresses.map((address, index) => (
                  <div
                    key={address.client_uuid || `${address.id || "new"}-${index}`}
                    className="rounded-md border border-white/20 bg-cblack3 p-3"
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h4 className="text-sm font-medium text-cwhite">
                        Address #{index + 1}
                      </h4>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-auto p-0 text-cyellow hover:bg-transparent hover:text-cyellow"
                          onClick={() => handleEditAddress(address.client_uuid)}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-auto p-0 text-cred hover:bg-transparent hover:text-cred"
                          onClick={() => handleRemoveAddress(address.client_uuid)}
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    </div>
                    <div className="text-xs text-cwhite2 space-y-1">
                      <p>Post Code: {address.postal_code || "N/A"}</p>
                      <p>Door No: {address.door_no || "N/A"}</p>
                      <p>Address: {address.address || "N/A"}</p>
                      <p>City: {address.city || "N/A"}</p>
                      <p>Note: {address.note || "N/A"}</p>
                      <p>
                        Status: {address.is_active ? "Active" : "Inactive"}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          </div>

            <div className="flex-shrink-0 px-8 py-4 border-t border-white/10 bg-cblack4">
              <Button
                type="submit"
                className="bg-cyellow w-full text-cblack hover:bg-cyellow/95"
                isLoading={formLoading}
              >
                {isEditMode ? "Update" : "Confirm"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AddNewCustomerDialog;
